import React, { useState, useEffect } from 'react';
import { useTeamOnboarding } from './useTeamOnboarding';
import { Spinner } from 'app/src/components/Spinner';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import RouterLink from 'app/common/components/singlespa/router-link';
import {
  useTeamOnboardingPupsQuery,
  useTeamOnboardingEnablePluginMutation,
  useDismissTeamOnboardingPupPopoverMutation,
} from '@trello/graphql/generated';
import { forNamespace } from 'app/common/lib/util/i18n';
import {
  Button,
  ButtonTypes,
  IconButton,
} from 'app/common/components/controls/button/button';
import { Icon, IconColor, IconName } from 'app/common/components/controls';
import { logError } from 'app/scripts/debug/log-error';
import { Feature } from 'app/scripts/debug/constants';

const format = forNamespace(['team onboarding', 'power-up-dialog']);

import styles from './TeamOnboardingPupPopover.less';

const DISPLAY_PUPS = ['Google Drive', 'Calendar', 'Slack'];

interface TeamOnboardingPupPopoverProps {
  orgId: string;
  boardId: string;
  onShowPowerUps(): void;
  onDismissed(): void;
  renderTimeout?: number;
}

export const TeamOnboardingPupPopover: React.FunctionComponent<TeamOnboardingPupPopoverProps> = ({
  orgId,
  boardId,
  onShowPowerUps,
  onDismissed,
  renderTimeout = 2500,
}) => {
  const [idPluginSaving, setIdPluginSaving] = useState<string>('');
  const [canRender, setCanRender] = useState<boolean>(false);
  const { isLoading, isEligible } = useTeamOnboarding(orgId);
  const { data, loading, refetch } = useTeamOnboardingPupsQuery({
    variables: {
      boardId,
    },
  });
  const [
    enablePlugin,
    { loading: savingPlugin },
  ] = useTeamOnboardingEnablePluginMutation();
  const [dismissPopover] = useDismissTeamOnboardingPupPopoverMutation();

  const oneTimeMessagesDismissed = data?.member?.oneTimeMessagesDismissed || [];
  const plugins = (data?.publicPlugins || []).filter(({ listing }) =>
    listing?.name ? DISPLAY_PUPS.includes(listing.name) : false,
  );
  const boardPlugins = data?.board?.boardPlugins || [];
  const activePluginIds = boardPlugins.map((p) => p.idPlugin);

  // this ensures that the plugin being added shows as loading
  // then "Added" once its added. If i did this with checking
  // the loading state of the mutation, it flashes Add before complete
  useEffect(() => {
    if (idPluginSaving) {
      if (!savingPlugin && activePluginIds.includes(idPluginSaving)) {
        setIdPluginSaving('');
      }
    }
  }, [savingPlugin, idPluginSaving, activePluginIds]);

  useEffect(() => {
    if (isEligible) {
      const renderTimeoutId = setTimeout(() => {
        setCanRender(true);
      }, renderTimeout);

      return function cleanup() {
        clearTimeout(renderTimeoutId);
      };
    }
  }, [canRender, isEligible, renderTimeout]);

  const isPluginEnabled = (pluginId: string) => {
    if (activePluginIds.includes(pluginId)) {
      return true;
    }
    return false;
  };

  const onClick = async (pluginId: string) => {
    try {
      setIdPluginSaving(pluginId);
      await enablePlugin({
        variables: { pluginId, boardId },
      });
      refetch();
    } catch (err) {
      logError({
        error: err,
        tags: {
          ownershipArea: 'trello-bizteam',
          feature: Feature.TeamOnboarding,
        },
      });
    }
  };

  const onDismiss = async () => {
    try {
      await dismissPopover({
        variables: {
          memberId: 'me',
          messageId: `bc-onboarding-pup-popover:${orgId}`,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          addOneTimeMessagesDismissed: {
            id: 'me',
            oneTimeMessagesDismissed: oneTimeMessagesDismissed!.concat([
              `bc-onboarding-pup-popover:${orgId}`,
            ]),
            __typename: 'Member',
          },
        },
      });
      onDismissed();
    } catch (err) {
      logError({
        error: err,
        tags: {
          ownershipArea: 'trello-bizteam',
          feature: Feature.TeamOnboarding,
        },
      });
    }
  };

  if (
    isLoading ||
    !isEligible ||
    loading ||
    !canRender ||
    // most things are covered in useTeamOnboarding
    // but this one requires the board so its done here
    data?.board?.enterpriseOwned
  ) {
    return null;
  }

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-bizteam',
        feature: Feature.TeamOnboarding,
      }}
    >
      <div className={styles.pupPopover}>
        <div className={styles.pupPopoverTaco}>
          <div className={styles.pupPopoverTacoInner}>
            <img alt="Taco" src={require('resources/images/taco.png')} />
          </div>
        </div>
        <div className={styles.pupPopoverInner}>
          <img
            className={styles.pupPopoverHeaderImg}
            alt="Powerups"
            src={require('resources/images/team-onboarding/powerups.jpg')}
          />
          <div className={styles.pupPopoverText}>
            <div className={styles.pupPopoverTitle}>{format('title')}</div>
            <p className={styles.pupPopoverDescription}>
              {format('description')}
            </p>
            &nbsp;
            <RouterLink href="/power-ups">{format('learn more')}</RouterLink>
            <div>
              {plugins.map((plugin) => {
                const isEnabled = isPluginEnabled(plugin.id);
                const isEnabling = plugin.id === idPluginSaving;
                let buttonText;
                if (isEnabling) {
                  buttonText = <Spinner centered small />;
                } else {
                  buttonText = isEnabled ? format('added') : format('add');
                }
                return (
                  <div key={plugin.id} className={styles.pupPopoverPlugin}>
                    <div className={styles.pupPopoverPluginTitle}>
                      <div className={styles.pupPopoverPluginImg}>
                        <img
                          alt={plugin?.listing?.name || ''}
                          src={plugin?.icon?.url || ''}
                        />
                      </div>
                      <p>{plugin?.listing?.name || ''}</p>
                    </div>
                    <Button
                      disabled={isEnabled || isEnabling}
                      className={styles.pupPopoverPluginButton}
                      onClick={() => onClick(plugin.id)}
                      buttonType={
                        isEnabled ? ButtonTypes.DISABLED : ButtonTypes.PRIMARY
                      }
                    >
                      {buttonText}
                    </Button>
                  </div>
                );
              })}
              <Button
                className={styles.pupPopoverSeeMorePluginsButton}
                onClick={onShowPowerUps}
                buttonType={ButtonTypes.ICON}
              >
                <Icon name={IconName.PowerUp} large color={IconColor.Gray} />
                {format('see-more')}
              </Button>
            </div>
            <IconButton
              onClick={onDismiss}
              className={styles.pupPopoverDismiss}
              icon={IconName.Close}
              color={IconColor.Dark}
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
